/**
 * AI-JAVADOC: PcCallProtocolComponent
 * Module: PC (Phone Call)
 * Type: ObjectBaseComponent
 * Description: Object component of one pc.CallProtocol. It owns the whole write flow of a protocol:
 *              it persists the record immediately on entry, offers the two capture paths (the
 *              metadata-driven form with the comment editor, and the AI dialog), drives the two
 *              status actions, and reports "back to the list" to its host instead of navigating.
 *
 * Round-based LLM dialog — design: projects/pc_call_obc/REQUIREMENT.md §8.3, §9 rules 1 and 11.
 */
import {Component, OnChanges, OnDestroy, OnInit} from "@angular/core";
import {Observable, Subscription} from "rxjs";

import {ObjectBaseComponent} from "../../../../../core/shared/object/mvs-object-base/object-base.component";
import {ObjectIdentifier} from "../../../../../core/shared/basic/object-identifier";
import {DtoDetail} from "../../../../../core/shared/dto/dto.detail";
import {DtoList} from "../../../../../core/shared/dto/dto.list";
import {FilterCriteria} from "../../../../../core/shared/filter/api/filter.criteria";
import {ObjectRequestList} from "../../../../../core/shared/dto/object/request/object-request-list";
import {ObjectRequestComplex} from "../../../../../core/shared/dto/object/request/object-request-complex";
import {ObjectRequestComplexNode} from "../../../../../core/shared/dto/object/request/object-request-complex-node";
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from "../../../../../core/shared/logic/object-change-information";
import {MvsFormControlOverwrite} from "../../../../../core/shared/logic/form/logic/mvs-form-control-overwrite";
import {MvsFormFieldAccessEnum} from "../../../../../core/shared/logic/form/logic/mvs-form-field-access.enum";

import {CallProtocolDto} from "../../../model/dto/entity/call-protocol.dto";
import {CallProtocolStatusEnum} from "../../../enum/call-protocol-status.enum";

/** The two capture paths the advisor can switch between at any time. */
export type PcCallProtocolCaptureMode = 'form' | 'ai';

/**
 * Object component of one {@code pc.CallProtocol}.
 *
 * <h3>There is no create branch — only edit</h3>
 * The component may be mounted WITHOUT an object id. In that case the host has handed the call
 * reference in through the native {@code defaultCreateDto} channel of {@code <mvs-object>}
 * (object.component.ts:136-150 assigns it to {@code instance.dto} before {@code ngOnInit} runs), and
 * {@link #persistInitialProtocol} writes the row immediately. From the moment that POST returns,
 * {@code objectIdentifier} carries a real id and EVERY further code path in this component — the
 * form, the AI dialog, the status actions — is an edit path. There is deliberately no second create
 * branch anywhere below.
 *
 * <h3>Two capture paths, switchable at any time</h3>
 * {@link #captureMode} decides whether the protocol is written in the metadata-driven CRUD form with
 * the {@code comment} editor, or through {@code <mvs-ai-dialog>}. The switch is offered whenever the
 * owning call type carries an AI dialog type, and it is a toggle, not a one-off decision: the advisor
 * may start in the form, continue in the dialog and go back. With no dialog type configured — the
 * normal case — the component behaves exactly like the generic object view it replaces
 * (REQUIREMENT.md §9 rule 1).
 *
 * <h3>Status: exactly two actions</h3>
 * "Abschließen" writes {@code CallProtocolStatusEnum.completed}, "Wieder öffnen" writes
 * {@code in_progress}. Nothing else may set the status here, and the generic status picker is
 * therefore hidden from the form (see {@link #buildFormControlOverwrite}).
 *
 * ASSUMED SEMANTICS, recorded here because it was not contradicted: a completed protocol is READ
 * ONLY until "Wieder öffnen" is pressed. Without that, the second action would have no function at
 * all. Read-only is enforced on the form ({@code readonly} + {@code hideDefaultActions}) and on the
 * AI dialog ({@code readOnly}, which also stops it from opening a fresh session over an already
 * final text — §9 rule 11).
 *
 * The two appended enum values {@code in_dialog} and {@code finalized} are NOT used by this flow.
 * They stay in the enum because the backend persists the enum by ordinal, but nothing here reads or
 * writes them.
 *
 * <h3>Back is reported, never navigated</h3>
 * "Zurück" is available at all times, including in the middle of an AI dialog. It emits the inherited
 * {@code onBackNavigation} output — {@code ObjectComponent} forwards it (object.component.ts:160) —
 * and the host decides what happens. This component never navigates.
 *
 * <h3>One request, not three</h3>
 * {@code call.callType.aiDialogType} used to be resolved with two extra serial GETs after the
 * protocol had already been loaded, on every protocol, even where no dialog type was configured.
 * {@link #getRetrieveObjectObservable} folds that into the load that happens anyway by pulling the
 * {@code call.callType} node with an {@code ObjectRequestComplex}, so {@link #aiDialogTypeId} is
 * available synchronously with the DTO.
 *
 * Round-based LLM dialog — design: projects/pc_call_obc/REQUIREMENT.md §8.3, §9.
 */
@Component({
    selector: 'mvs-pc-call-protocol',
    templateUrl: './pc-call-protocol.component.html',
    standalone: false
})
export class PcCallProtocolComponent extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy {

    dto: CallProtocolDto;

    /**
     * Every subscription this component opens.
     *
     * {@code ObjectComponent.ngOnChanges} (object.component.ts:233-256) destroys and recreates the
     * dynamically created object component on every {@code objectId} change, so clicking quickly
     * through a protocol list leaves in-flight requests that would otherwise write into a destroyed
     * instance. Cleared in {@link #ngOnDestroy}.
     */
    protected subscriptions: Subscription[] = [];

    /** Which capture path is on screen. Never 'ai' without {@link #aiDialogTypeId}. */
    captureMode: PcCallProtocolCaptureMode = 'form';

    /** Options of the capture switch. Held here so the template does not rebuild them per cycle. */
    readonly captureModeOptions: { label: string, value: PcCallProtocolCaptureMode }[] = [
        {label: 'Eingabe', value: 'form'},
        {label: 'KI', value: 'ai'}
    ];

    /**
     * The dialog type configured on the owning call's call type, or null when the feature is not
     * switched on for it. Null keeps every code path below on the pre-existing behaviour.
     */
    aiDialogTypeId: number = null;

    /** The immediate initial save is in flight. */
    creating: boolean = false;

    /** Hides the generic protocol status picker from the metadata-driven form. */
    formControlOverwrite: MvsFormControlOverwrite;

    /** Whether a status write is currently in flight; keeps the two actions from double firing. */
    statusChanging: boolean = false;

    /**
     * The embedded form holds edits the advisor has not saved yet.
     *
     * Tracked separately from the inherited {@code isDirty}, which is one-way by contract (see
     * {@code ObjectBaseComponent#markAsDirty}) and would leave the completion action disabled forever
     * after the first keystroke. This flag is cleared again on every save and on every reload.
     */
    formDirty: boolean = false;

    /**
     * The object could not be loaded or, on the create path, not be written.
     *
     * Needed because the base error handler blanks {@code dto} and {@code initialized}, which used to
     * leave a completely empty panel behind: the "Zurück" button sat INSIDE the same guard, the host
     * stays in its edit state, and {@code ObjectComponent} forwards no load-error output — so there
     * was no way out but a page reload.
     */
    loadError: boolean = false;

    /** What went wrong, in the advisor's words. */
    loadErrorMessage: string = null;

    // -------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------

    /**
     * Entry point for BOTH mount cases.
     *
     * Without an object id the base initialization is deliberately NOT run yet: it would end in
     * {@code crudService.get(null)}. The record is written first, and only then does the normal
     * object lifecycle start — with a real id, in edit.
     */
    override ngOnInit(): void {

        this.formControlOverwrite = this.buildFormControlOverwrite();

        if (this.objectIdentifier && !this.objectIdentifier.objectId) {
            this.persistInitialProtocol();
            return;
        }

        super.ngOnInit();

    }

    /**
     * Writes the protocol row from the default DTO the host supplied, so that a {@code pc.CallProtocol}
     * record exists before the advisor types anything. On success the component continues as a plain
     * edit component.
     */
    protected persistInitialProtocol(): void {

        this.clearLoadError();

        const callId = this.resolveCallId();

        if (!callId) {
            // Without the call reference the row cannot be written — pc.CallProtocol.call is mandatory.
            this.loadErrorMessage = "Das Protokoll konnte nicht angelegt werden, weil der Anruf fehlt.";
            this.postRefreshObjectError({error: "No call reference provided for the new protocol."});
            return;
        }

        this.creating = true;
        this.busy = true;

        const newDto = new CallProtocolDto();
        newDto.callDtoId = callId;
        newDto.protocolStatus = CallProtocolStatusEnum.in_progress;
        // Kept from the previous auto-create: the row is written before the advisor has entered
        // anything, and a blank comment is what the editor then opens on.
        newDto.comment = " ";

        const crudService = this.coreService.getCrudService(this.objectIdentifier.objectType);

        if (!crudService) {
            this.creating = false;
            this.loadErrorMessage = "Das Protokoll konnte nicht angelegt werden.";
            this.postRefreshObjectError({error: "No CRUD Service defined."});
            return;
        }

        const subscription = crudService.create(newDto, this.importObjectContext).subscribe({
            next: (created: DtoDetail) => {

                this.creating = false;

                // From here on the component is an edit component on a real row.
                this.objectIdentifier = new ObjectIdentifier(this.objectIdentifier.objectType, created["id"]);

                this.onChangedObject.emit(ObjectChangeInformation.create(
                    this.objectIdentifier.objectType,
                    ObjectChangeInformationActionEnum.created,
                    null,
                    created));

                super.ngOnInit();

            },
            error: (error: any) => {
                this.creating = false;
                this.loadErrorMessage = "Das Protokoll konnte nicht angelegt werden.";
                this.postRefreshObjectError(error);
            }
        });

        this.subscriptions.push(subscription);

    }

    /**
     * The call the new protocol belongs to. Primary source is the default DTO handed in through
     * {@code <mvs-object [defaultCreateDto]>}; the import object context is the fallback for hosts
     * that only supply the context.
     */
    protected resolveCallId(): number {

        const defaultDto = <CallProtocolDto>this.dto;

        if (defaultDto?.callDtoId) {
            return defaultDto.callDtoId;
        }

        const contextEntry = this.importObjectContext?.entries
            ?.find(entry => entry.objectTypeAlias === 'pc.Call');

        return contextEntry?.id ?? null;

    }

    /**
     * Cancels everything still in flight. See {@link #subscriptions} for why this matters here.
     */
    override ngOnDestroy(): void {

        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }

        this.subscriptions = [];

        super.ngOnDestroy();

    }

    // -------------------------------------------------------------------------
    // Loading
    // -------------------------------------------------------------------------

    /**
     * Loads the protocol AND the {@code call.callType} node in one request, so that the configured
     * AI dialog type is known at the same moment as the protocol itself.
     */
    protected override getRetrieveObjectObservable(): Observable<any> {

        const filterCriteria = FilterCriteria.create('id', FilterCriteria.cOperatorEqual, this.objectIdentifier.objectId);

        const objectRequestList = ObjectRequestList.createBasic(true, [filterCriteria], []);

        objectRequestList.objectRequestComplex = ObjectRequestComplex.build(
            true,
            false,
            ObjectRequestComplexNode.createSimpleAttributeNode('call')
                .addNodes(ObjectRequestComplexNode.createSimpleAttributeNode('callType'))
        );

        return this.crudService.list(objectRequestList);

    }

    /**
     * Unwraps the single entry of the list request above and keeps the subscription cancellable.
     */
    protected override retrieveObject(): void {

        this.busy = true;

        this.clearLoadError();

        if (this.transientDto) {
            this.storeNewDto(this.transientDto);
            return;
        }

        const subscription = this.getRetrieveObjectObservable().subscribe({
            next: (dtoList: DtoList) => {

                const dto = dtoList?.entries?.[0];

                if (!dto) {
                    this.postRefreshObjectError({error: "Protocol not found."});
                    return;
                }

                this.storeNewDto(dto);

            },
            error: (error: any) => {
                this.postRefreshObjectError(error);
            }
        });

        this.subscriptions.push(subscription);

    }

    /**
     * The object was (re)loaded. Nothing here triggers another load.
     */
    override onObjectChanged(): void {

        this.aiDialogTypeId = this.dto?.callDto?.callTypeDto?.aiDialogTypeDtoId ?? null;

        if (!this.aiDialogTypeId) {
            this.captureMode = 'form';
        }

        // Every reload this component triggers also changes an input of the embedded form — the
        // read-only flip after a status write, the capture switch after a finalize — so the form is
        // rebuilt and whatever was in it is gone with it.
        this.formDirty = false;

        if (this.dto?.id) {
            this.setPageTitle(`Protokoll #${this.dto.id}`);
        }

    }

    /**
     * Records the failure so the template can say what happened instead of rendering nothing. The
     * base implementation blanks {@code dto} and {@code initialized}, which is why the toolbar with
     * the "Zurück" button lives OUTSIDE that guard in the template.
     */
    override postRefreshObjectError(error: any): void {

        this.loadError = true;

        if (!this.loadErrorMessage) {
            this.loadErrorMessage = "Das Protokoll konnte nicht geladen werden.";
        }

        super.postRefreshObjectError(error);

    }

    /** Drops a previous failure before a new attempt. */
    protected clearLoadError(): void {

        this.loadError = false;
        this.loadErrorMessage = null;

    }

    // -------------------------------------------------------------------------
    // Capture mode
    // -------------------------------------------------------------------------

    /**
     * Switches between the metadata-driven form and the AI dialog. Available in edit as well — the
     * decision is never final.
     */
    onCaptureModeChange(mode: PcCallProtocolCaptureMode): void {

        if (mode === 'ai' && !this.aiDialogTypeId) {
            return;
        }

        // Switching tears the form down, so anything unsaved in it is gone either way — the flag
        // must not survive and block the completion action for the rest of the session.
        this.formDirty = false;

        this.captureMode = mode;

    }

    /**
     * The AI dialog wrote its text into the protocol. Switch back to the form so the advisor sees the
     * written text, reload the object, and tell the host that the protocol changed.
     */
    onAiDialogFinalized(): void {

        this.captureMode = 'form';

        this.emitProtocolUpdated();
        this.refreshObject(false);

    }

    /**
     * The metadata-driven form saved. Pass it on so the host can refresh its list, and drop the
     * unsaved-input flag — what was in the form is now in the database.
     */
    onFormChangedObject(event: ObjectChangeInformation): void {

        this.formDirty = false;

        this.onChangedObject.emit(event);

    }

    /**
     * The embedded form reports that it holds unsaved input.
     *
     * Two things happen here. {@link #formDirty} gates the completion action below, so the advisor
     * cannot lock a protocol while an edit of it is still sitting in the form unsaved. And
     * {@code markAsDirty()} is called so the INHERITED dirty contract finally reports the truth:
     * without it {@code isComponentDirty()} answered {@code false} for the whole lifetime of the
     * component and every dirty guard of every host considered it clean.
     */
    onFormDirtyChange(dirty: boolean): void {

        if (!dirty) {
            return;
        }

        this.formDirty = true;

        this.markAsDirty();

    }

    // -------------------------------------------------------------------------
    // Status — exactly two actions
    // -------------------------------------------------------------------------

    /** True while the protocol is final and therefore read only. */
    isCompleted(): boolean {
        return this.dto?.protocolStatus === CallProtocolStatusEnum.completed;
    }

    /** "Abschließen" — the protocol is done and becomes read only. */
    onCompleteProtocol(): void {
        this.writeProtocolStatus(CallProtocolStatusEnum.completed);
    }

    /** "Wieder öffnen" — the protocol becomes editable again. */
    onReopenProtocol(): void {
        this.writeProtocolStatus(CallProtocolStatusEnum.in_progress);
    }

    /**
     * The ONLY place that writes {@code protocolStatus} in this view.
     *
     * <h3>The update DTO carries the status and NOTHING else</h3>
     * It is assembled field by field for two separate reasons, and both matter.
     *
     * The first is unchanged: the loaded DTO carries the resolved {@code call.callType} graph from
     * {@link #getRetrieveObjectObservable}, and sending that back would push read-only resolution
     * results into a write.
     *
     * The second is the one that cost a protocol its text. {@code this.dto} is the state as of the
     * last LOAD. It is never refreshed while the advisor works, because the embedded
     * {@code <mvs-crud-object>} loads its own copy through {@code [objectId]} and shares no object
     * with this component: {@link #onFormChangedObject} only forwards the event, and
     * {@code ObjectBaseComponent#postObjectObservationRefresh} (object-base.component.ts:150-152) is
     * empty. {@code MvsCrudService#updateInternal} (mvs-crud.service.ts:732-750) then PUTs whatever
     * it is handed. Sending {@code comment} from {@code this.dto} therefore overwrote the text the
     * advisor had just saved with the value loaded before he typed it — and because the same action
     * flips the view to read-only, the protocol was left EMPTY and locked.
     *
     * So: send only what the status action actually changes. Every other field is a field the user
     * may have changed in the meantime without this component being told:
     *   - {@code comment}    — edited in the form. THE defect above.
     *   - {@code callDtoId}  — rendered by the form as "Anruf" and not hidden by
     *                          {@link #buildFormControlOverwrite}, so it is editable too.
     * An undefined property is not serialized, so the server leaves those columns alone.
     *
     * Reloading before the write would NOT be a fix: it would discard input the advisor has typed
     * but not yet saved. Sending less is the only safe direction here.
     */
    protected writeProtocolStatus(status: CallProtocolStatusEnum): void {

        if (!this.dto?.id || this.statusChanging) {
            return;
        }

        this.statusChanging = true;

        const updateDto = new CallProtocolDto();
        updateDto.id = this.dto.id;
        updateDto.protocolStatus = status;

        const subscription = this.crudService.update(updateDto).subscribe({
            next: () => {
                this.statusChanging = false;
                this.emitProtocolUpdated();
                this.refreshObject(false);
            },
            error: () => {
                this.statusChanging = false;
                this.messageService.showErrorMessage(
                    "Protokoll",
                    "Der Status des Protokolls konnte nicht geändert werden.");
            }
        });

        this.subscriptions.push(subscription);

    }

    // -------------------------------------------------------------------------
    // Back
    // -------------------------------------------------------------------------

    /**
     * "Zurück" — reported to the host, which owns the navigation. Available at any time, including
     * while an AI dialog is running: the dialog persists every round server side, so leaving it does
     * not lose anything.
     */
    onBack(): void {
        this.onBackNavigation.emit();
    }

    // -------------------------------------------------------------------------
    // Helpers
    // -------------------------------------------------------------------------

    /**
     * Hides the generic protocol status picker: the status is driven by the two toolbar actions only,
     * so a free choice in the form would be a second, contradicting way to set it.
     */
    protected buildFormControlOverwrite(): MvsFormControlOverwrite {

        const formControlOverwrite = new MvsFormControlOverwrite();
        formControlOverwrite.addField('protocolStatus', MvsFormFieldAccessEnum.HIDDEN);

        return formControlOverwrite;

    }

    /** Informs the host that this protocol changed, so its list can pick the new state up. */
    protected emitProtocolUpdated(): void {

        this.onChangedObject.emit(ObjectChangeInformation.create(
            this.objectIdentifier.objectType,
            ObjectChangeInformationActionEnum.updated,
            null,
            this.dto));

    }

}
